import React, { useState } from 'react'
import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import Checkbox from '@mui/material/Checkbox'
import FormControl from '@mui/material/FormControl'
import FormControlLabel from '@mui/material/FormControlLabel'
import MenuItem from '@mui/material/MenuItem'
import TextField from '@mui/material/TextField'
import Typography from '@mui/material/Typography'
import GifticonProductPickerDialog from './GifticonProductPickerDialog'
import {
    APP_RANKING_BANNER_REWARD_TYPES,
    getAppRankingBannerPeriodUnitLabel,
    isPaidAppRankingBannerReward,
} from './appRankingBannerDraftUtils'

const REWARD_TYPE_OPTIONS = [
    { value: APP_RANKING_BANNER_REWARD_TYPES.NONE, label: '전시만 (경품 이미지만 노출)' },
    { value: APP_RANKING_BANNER_REWARD_TYPES.POINT, label: '포인트 지급' },
    { value: APP_RANKING_BANNER_REWARD_TYPES.GIFTICON, label: '기프티콘 지급' },
]

const createFieldChangeEvent = (name, value) => ({ target: { name, value } })

// 경품 지급 규칙 입력. 등록/수정 화면이 같은 규칙을 쓰도록 한 컴포넌트로 모았다.
const GIFTICON_POINT_MULTIPLIER = 3

export default function AppRankingBannerRewardFields({ params = {}, disabled = false, onChange, goodsName = '' }) {
    const [pickerOpen, setPickerOpen] = useState(false)
    const rewardType = params.rewardType || APP_RANKING_BANNER_REWARD_TYPES.NONE
    const isPaid = isPaidAppRankingBannerReward(rewardType)
    const isGifticon = rewardType === APP_RANKING_BANNER_REWARD_TYPES.GIFTICON
    const periodUnitLabel = getAppRankingBannerPeriodUnitLabel(params.type)
    const isMonthly = Number(params.type) === 2 || Number(params.type) === 4
    const selectedGoodsLabel = params.goodsCode
        ? `${goodsName || params.goodsName || '선택한 상품'} (${params.goodsCode})`
        : ''

    // 기프티콘을 못 보냈을 때 대신 줄 수 있는 포인트. 상품을 고르면 판매가로 계산하고,
    // 수정 화면에서는 서버가 내려준 값을 쓴다.
    const convertPoint = Number(params.convertPoint || 0)

    const handleGoodsSelect = (goods) => {
        onChange(createFieldChangeEvent('goodsCode', goods.goodsCode))
        onChange(createFieldChangeEvent('goodsName', goods.goodsName || ''))
        onChange(createFieldChangeEvent('convertPoint', Math.floor(Number(goods.salePrice || 0)) * GIFTICON_POINT_MULTIPLIER))
        setPickerOpen(false)
    }

    return (
        <>
            <FormControl fullWidth>
                <TextField
                    select
                    label="경품 종류"
                    name="rewardType"
                    value={rewardType}
                    disabled={disabled}
                    onChange={onChange}
                    size="small"
                    helperText="지급 규칙을 고르면 적용 기간을 반드시 지정해야 합니다."
                >
                    {REWARD_TYPE_OPTIONS.map((option) => (
                        <MenuItem key={option.value} value={option.value}>{option.label}</MenuItem>
                    ))}
                </TextField>
            </FormControl>

            <FormControl fullWidth>
                <TextField
                    label="끝 순위 (범위 경품)"
                    name="rankTo"
                    value={params.rankTo || ''}
                    disabled={disabled}
                    onChange={onChange}
                    size="small"
                    helperText="비우면 시작 순위 한 명에게만 지급합니다. 예) 시작 2, 끝 5 → 2~5위"
                />
            </FormControl>

            {rewardType === APP_RANKING_BANNER_REWARD_TYPES.POINT && (
                <FormControl fullWidth>
                    <TextField
                        required
                        label="지급 포인트"
                        name="pointAmount"
                        value={params.pointAmount || ''}
                        disabled={disabled}
                        onChange={onChange}
                        size="small"
                        helperText="범위 경품이면 범위 안 모든 순위에 같은 포인트를 지급합니다."
                    />
                </FormControl>
            )}

            {isGifticon && (
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                    <Box sx={{ display: 'flex', gap: 1, alignItems: 'flex-start' }}>
                        <TextField
                            fullWidth
                            label="기프티콘 상품"
                            value={selectedGoodsLabel}
                            placeholder="상품을 선택하세요"
                            size="small"
                            disabled={disabled}
                            InputProps={{ readOnly: true }}
                        />
                        <Button
                            variant="outlined"
                            size="small"
                            disabled={disabled}
                            onClick={() => setPickerOpen(true)}
                            sx={{ whiteSpace: 'nowrap', mt: 0.25 }}
                        >
                            상품 검색
                        </Button>
                    </Box>
                    <FormControlLabel
                        control={(
                            <Checkbox
                                name="useGoodsImage"
                                checked={Boolean(params.useGoodsImage)}
                                disabled={disabled || !params.goodsCode}
                                onChange={(event) => onChange(createFieldChangeEvent('useGoodsImage', event.target.checked))}
                            />
                        )}
                        label="상품 이미지 사용 (기프티쇼 상품 이미지를 가져와 경품 이미지로 저장합니다)"
                    />
                    <Typography variant="caption" color="text.secondary">
                        상품 이미지를 쓰면 이미지를 직접 올리지 않아도 되고, 경품과 이미지가 어긋나지 않습니다.
                    </Typography>
                    {convertPoint > 0 && (
                        <Typography variant="caption" color="text.secondary">
                            환산 포인트: <b>{convertPoint.toLocaleString()}P</b> (권장 소비자가 × 3)
                            — 기프티콘을 보내지 못했을 때 대신 줄 수 있는 포인트입니다. 앱에도 함께 표시됩니다.
                        </Typography>
                    )}
                </Box>
            )}

            <Box sx={{ display: 'flex', gap: 1 }}>
                <TextField
                    fullWidth
                    type="date"
                    label={`적용 기간 시작 (${isMonthly ? '1일' : '월요일'})`}
                    name="applyFrom"
                    value={params.applyFrom || ''}
                    disabled={disabled}
                    onChange={onChange}
                    size="small"
                    required={isPaid}
                    InputLabelProps={{ shrink: true }}
                />
                <TextField
                    fullWidth
                    type="date"
                    label={`적용 기간 끝 (${isMonthly ? '말일' : '일요일'})`}
                    name="applyTo"
                    value={params.applyTo || ''}
                    disabled={disabled}
                    onChange={onChange}
                    size="small"
                    required={isPaid}
                    InputLabelProps={{ shrink: true }}
                />
            </Box>
            <Typography variant="caption" color="text.secondary">
                적용 기간은 순위 기간 단위({periodUnitLabel})로 저장됩니다. 기간 중간 날짜를 골라도 그 {isMonthly ? '달' : '주'}에 맞춰집니다.
                노출 중인 다른 규칙과 순위·기간이 겹치면 저장되지 않습니다.
            </Typography>

            <GifticonProductPickerDialog
                open={pickerOpen}
                onClose={() => setPickerOpen(false)}
                onSelect={handleGoodsSelect}
            />
        </>
    )
}
